// Étape finale : préview PDF exacte (PDFViewer = même gabarit que le fichier final),
// choix du dossier de classement, options parapheur / partage client, rangement GED.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { parapheurCircuitPath } from '../../services/signature/parapheurRoutes';
import { PDFViewer } from '@react-pdf/renderer';
import { ArrowLeft, FolderOpen } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/useAuthStore';
import { canAccessFeature, asCollaboratorRole } from '../../services/auth/permissions';
import { useTenantBranding } from '../../contexts/ThemeContext';
import { useAllDossierFolders } from '../../hooks/queries/useDossierFiles';
import { uploadFile, replaceFileContents, setFileVisibility } from '../../services/files/filesService';
import { finalizeDraft } from '../../services/documents/documentService';
import { docKeys, usePdfLayouts, useDocumentTemplates } from '../../hooks/queries/useDocumentGeneration';
import { resolveLayoutTokens } from '../../services/documents/pdfLayoutService';
import { tiptapToPdfBlocks, type TiptapNode } from '../../services/documents/markdownDoc';
import { DocumentPdfDoc, buildDocumentPdfBlob, type DocumentPdfProps } from './DocumentPdf';
import { CreateCircuitModal } from '../signature/CreateCircuitModal';

interface Props {
  dossierId: string;
  draftId: string;
  title: string;
  doc: TiptapNode;
  recipientLines: string[];
  templateId: string | null;
  onDone: () => void;
  onBack: () => void;
  replaceFileId?: string | null;
  onRevisionSaved?: () => void;
}

export function DocumentFinalizeStep({ dossierId, draftId, title, doc, recipientLines, templateId, onDone, onBack, replaceFileId, onRevisionSaved }: Props) {
  const navigate = useNavigate();
  const collaborator = useAuthStore((s) => s.collaborator);
  const role = asCollaboratorRole(collaborator?.role);
  const canCircuit = role ? canAccessFeature(role, 'signature_circuit_create') : false;
  const { branding } = useTenantBranding();
  const { data: folders = [] } = useAllDossierFolders(dossierId);
  const { data: layouts } = usePdfLayouts();
  const { data: templates } = useDocumentTemplates(false);
  const qc = useQueryClient();

  const layoutTokens = useMemo(() => {
    const tpl = templates?.find((t) => t.id === templateId);
    const row =
      (tpl?.pdf_layout_id ? layouts?.find((l) => l.id === tpl.pdf_layout_id) : undefined)
      ?? layouts?.find((l) => l.is_default);
    return resolveLayoutTokens(row);
  }, [templates, templateId, layouts]);

  const [folderId, setFolderId] = useState<string>('');
  const [toParapheur, setToParapheur] = useState(false);
  const [shareClient, setShareClient] = useState(false);
  const [saving, setSaving] = useState(false);
  const [circuitFile, setCircuitFile] = useState<{ id: string; name: string } | null>(null);
  const [uploaded, setUploaded] = useState<{ id: string; name: string } | null>(null);

  const pdfProps: DocumentPdfProps = useMemo(() => ({
    firmName: branding?.firm_name ?? 'CIME Avocats',
    logoUrl: branding?.logo_url ?? null,
    colorPrimary: branding?.color_primary ?? '#043429',
    colorGold: branding?.color_gold ?? '#c9a84c',
    title,
    recipientLines,
    dateLine: `Le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`,
    blocks: tiptapToPdfBlocks(doc),
    signerName: collaborator?.full_name ?? '',
    layout: layoutTokens,
  }), [branding, title, doc, recipientLines, collaborator, layoutTokens]);

  const finalize = async () => {
    if (!collaborator) return;
    setSaving(true);
    try {
      const blob = await buildDocumentPdfBlob(pdfProps);
      const safeName = `${title.replace(/[\\/:*?"<>|]/g, '-').slice(0, 120)}.pdf`;
      const file = new File([blob], safeName, { type: 'application/pdf' });

      if (replaceFileId) {
        await replaceFileContents(replaceFileId, file);
        if (shareClient) await setFileVisibility(replaceFileId, 'shared_client');
        await finalizeDraft(draftId, replaceFileId);
        qc.invalidateQueries({ queryKey: docKeys.drafts(dossierId) });
        qc.invalidateQueries({ queryKey: ['dossier-files'] });
        qc.invalidateQueries({ queryKey: ['dossier-files-all', dossierId] });
        toast.success('Nouvelle version rangée dans la GED.');
        onRevisionSaved?.();
        onDone();
        return;
      }

      let fileRef = uploaded;
      if (!fileRef) {
        const uploadedFile = await uploadFile({
          dossierId,
          folderId: folderId || null,
          uploaderId: collaborator.id,
          file,
          source: 'ai_generated',
          sourceRef: `draft::${draftId}`,
        });
        fileRef = { id: uploadedFile.id, name: safeName };
        setUploaded(fileRef);
      }
      if (shareClient) await setFileVisibility(fileRef.id, 'shared_client');
      await finalizeDraft(draftId, fileRef.id);
      qc.invalidateQueries({ queryKey: docKeys.drafts(dossierId) });
      qc.invalidateQueries({ queryKey: ['dossier-files'] });
      qc.invalidateQueries({ queryKey: ['dossier-files-all', dossierId] });
      toast.success(`Document rangé dans la GED${shareClient ? ' et partagé au client' : ''}.`);
      if (toParapheur && canCircuit) {
        setCircuitFile({ id: fileRef.id, name: fileRef.name });
      } else {
        onDone();
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Échec du rangement du document.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full min-h-[60vh] flex-col gap-4 lg:flex-row">
      <div className="min-h-[420px] flex-1 overflow-hidden rounded border border-neutral-200">
        <PDFViewer width="100%" height="100%" showToolbar={false} style={{ border: 'none', minHeight: 420 }}>
          <DocumentPdfDoc {...pdfProps} />
        </PDFViewer>
      </div>

      <div className="w-full shrink-0 space-y-4 lg:w-72">
        <div>
          <label className="block text-sm">
            <span className="flex items-center gap-1.5 font-medium text-neutral-700">
              <FolderOpen className="w-4 h-4" aria-hidden /> Ranger dans
            </span>
            <select
              className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 text-sm"
              value={folderId}
              disabled={!!uploaded}
              onChange={(e) => setFolderId(e.target.value)}
            >
              <option value="">Racine du dossier</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            {uploaded && (
              <p className="mt-1 text-xs text-amber-700">Le fichier est déjà rangé — cliquez à nouveau pour terminer la finalisation.</p>
            )}
          </label>
        </div>

        {canCircuit && !replaceFileId && (
          <label className="flex cursor-pointer items-center gap-2 text-sm text-neutral-700">
            <input type="checkbox" checked={toParapheur} onChange={(e) => setToParapheur(e.target.checked)} />
            Envoyer au parapheur (validation interne)
          </label>
        )}
        <label className="flex cursor-pointer items-center gap-2 text-sm text-neutral-700">
          <input type="checkbox" checked={shareClient} onChange={(e) => setShareClient(e.target.checked)} />
          Partager au client (visible sur son portail)
        </label>

        <div className="flex flex-col gap-2 pt-2">
          <button type="button"
            className="rounded bg-cime-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50 cursor-pointer"
            disabled={saving}
            onClick={finalize}>
            {saving ? 'Rangement en cours…' : 'Valider et ranger dans la GED'}
          </button>
          <button type="button"
            className="flex items-center justify-center gap-1.5 rounded border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600 cursor-pointer"
            disabled={saving || !!uploaded}
            onClick={onBack}>
            <ArrowLeft className="w-4 h-4" aria-hidden /> Revenir à l’édition
          </button>
        </div>
      </div>

      {circuitFile && (
        <CreateCircuitModal
          fileId={circuitFile.id}
          fileName={circuitFile.name}
          onClose={() => { setCircuitFile(null); onDone(); }}
          onCreated={(id) => {
            setCircuitFile(null);
            toast.success('Circuit créé — posez les cases de paraphe sur le PDF.');
            onDone();
            navigate(parapheurCircuitPath(id));
          }}
        />
      )}
    </div>
  );
}
